/**
 * V67: 알림 마스터 — system_admin 전용 hook
 *
 * - listNotificationTypes: 전체 알림 type 메타 + 추가 수신자
 * - addNotificationRecipient / removeNotificationRecipient
 * - searchNotificationUsers: 사용자 검색
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

/** V68: 회사 필터 */
export type RecipientCompanyCode = 'KR' | 'CN' | 'ALL';

export interface AdditionalRecipient {
  type: 'USER' | 'EMAIL';
  email: string;
  userId?: string;
  displayName?: string;
  addedBy: string;
  addedAt: string;
  /** V68: 회사 필터. 미지정 = 'ALL' (모든 회사 알림 수신, backward compat) */
  companyCode?: RecipientCompanyCode;
}

export interface NotificationTypeDoc {
  type: string;
  title: string;
  description: string;
  trigger: string;
  /** V68: 운영자용 자연어 기본 수신자 설명 (읽기 전용, 코드 정책 미러) */
  defaultRecipientsLabel?: string;
  category: string;
  additionalRecipients: AdditionalRecipient[];
  updatedAt?: string;
  updatedBy?: string;
}

interface ListResponse {
  success: boolean;
  items: NotificationTypeDoc[];
  totalCount: number;
  /** V68: 현재 환경의 DL 매핑 (읽기 전용) */
  dlMap?: Record<string, { email: string; name: string }>;
  /** V68: 현재 GCP 프로젝트 ID */
  projectId?: string;
}

export function useNotificationTypes() {
  const [items, setItems] = useState<NotificationTypeDoc[]>([]);
  const [dlMap, setDlMap] = useState<Record<string, { email: string; name: string }>>({});
  const [projectId, setProjectId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fn = httpsCallable<Record<string, never>, ListResponse>(
        getFunctions(),
        'listNotificationTypes'
      );
      const r = await fn({});
      if (!r.data.success) throw new Error('조회 실패');
      setItems(r.data.items);
      setDlMap(r.data.dlMap || {});
      setProjectId(r.data.projectId || '');
    } catch (e: any) {
      console.error('[V67] listNotificationTypes 실패:', e);
      setError(new Error(e.message || '조회 실패'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { items, dlMap, projectId, isLoading, error, refetch };
}

export async function callAddNotificationRecipient(input: {
  notificationType: string;
  recipientType: 'USER' | 'EMAIL';
  email: string;
  userId?: string;
  displayName?: string;
  /** V68: 회사 필터. 미지정 시 BE 에서 'ALL' 로 저장. */
  companyCode?: RecipientCompanyCode;
}): Promise<AdditionalRecipient> {
  const fn = httpsCallable<typeof input, { success: boolean; recipient: AdditionalRecipient }>(
    getFunctions(),
    'addNotificationRecipient'
  );
  const r = await fn(input);
  return r.data.recipient;
}

export async function callRemoveNotificationRecipient(input: {
  notificationType: string;
  email: string;
  /** V68: 회사 필터. 미지정 시 해당 이메일의 모든 companyCode entry 삭제 (backward compat). */
  companyCode?: RecipientCompanyCode;
}): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean; removedCount: number }>(
    getFunctions(),
    'removeNotificationRecipient'
  );
  await fn(input);
}

export interface SearchUserItem {
  userId: string;
  email: string;
  displayName: string;
  orgUnitName?: string;
}

export async function callSearchNotificationUsers(searchText: string): Promise<SearchUserItem[]> {
  const fn = httpsCallable<{ searchText: string }, { success: boolean; users: SearchUserItem[]; totalCount: number }>(
    getFunctions(),
    'searchNotificationUsers'
  );
  const r = await fn({ searchText });
  return r.data.users;
}
